import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
    Plus,
    Trash2,
    Edit,
    User,
    Shield,
    CheckCircle2,
    XCircle,
    Search,
    X,
    Activity,
    Users,
    Mail,
    Phone,
    Loader2,
    MoreVertical
} from 'lucide-react';
import api from '../../services/api';
import toast from 'react-hot-toast';

const UserManager = () => {
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [editingUser, setEditingUser] = useState(null);
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        role: 'user',
        status: 'active'
    });

    useEffect(() => {
        fetchUsers();
    }, []);

    const [stats, setStats] = useState({ total: 0, active: 0, newThisWeek: 0 });

    const fetchUsers = async () => {
        try {
            const token = localStorage.getItem('token');
            const res = await api.get(`/users?search=${searchTerm}`);
            if (res.data.success) {
                setUsers(res.data.data);
                if (res.data.stats) setStats(res.data.stats);
            }
        } catch (error) {
            console.error('Error fetching users:', error);
            toast.error('Failed to load user directory');
        } finally {
            setLoading(false);
        }
    };

    // Refetch when search term changes with debounce
    useEffect(() => {
        const timer = setTimeout(() => {
            fetchUsers();
        }, 500);
        return () => clearTimeout(timer);
    }, [searchTerm]);

    const handleUpdateUser = async (e) => {
        e.preventDefault();
        try {
            const token = localStorage.getItem('token');
            const res = await api.put(`/users/${editingUser._id}`, formData);

            if (res.data.success) {
                toast.success('User protocol updated');
                closeModal();
                fetchUsers();
            }
        } catch (error) {
            toast.error(error.response?.data?.message || 'Update failed');
        }
    };

    const toggleStatus = async (user) => {
        const newStatus = user.status === 'active' ? 'disabled' : 'active';
        try {
            const token = localStorage.getItem('token');
            await api.put(`/users/${user._id}`, { status: newStatus });
            toast.success(`User set to ${newStatus}`);
            fetchUsers();
        } catch (error) {
            toast.error('Failed to toggle status');
        }
    };

    const handleEdit = (user) => {
        setEditingUser(user);
        setFormData({
            name: user.name,
            email: user.email,
            role: user.role,
            status: user.status
        });
        setIsEditModalOpen(true);
    };

    const closeModal = () => {
        setIsEditModalOpen(false);
        setEditingUser(null);
    };

    const handleDeleteUser = async (id) => {
        if (!window.confirm('CRITICAL: Purge user from system? This action is irreversible.')) return;
        try {
            const token = localStorage.getItem('token');
            await api.delete(`/users/${id}`);
            toast.success('User purged from registry');
            fetchUsers();
        } catch (error) {
            toast.error('Failed to purge user');
        }
    };

    const filteredUsers = users;

    return (
        <div className="space-y-6 animate-in fade-in duration-500 pb-10">
            {/* Header Section */}
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 border-b border-slate-800 pb-6">
                <div>
                    <h1 className="text-3xl font-bold text-white tracking-tight">User Management</h1>
                    <p className="text-slate-400 mt-1 text-sm">Manage system access and roles</p>
                </div>
                <div className="flex items-center gap-4">
                    <div className="relative group w-64">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 group-focus-within:text-violet-400 transition-colors" size={18} />
                        <input
                            type="text"
                            placeholder="Search users..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="w-full bg-slate-900 border border-slate-700 rounded-xl pl-10 pr-4 py-2.5 text-sm font-medium text-white placeholder:text-slate-500 focus:outline-none focus:border-violet-500/50 focus:ring-1 focus:ring-violet-500/50 transition-all"
                        />
                    </div>
                </div>
            </div>

            {/* Stats Overview */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="bg-gradient-to-br from-violet-600 to-indigo-700 p-6 rounded-2xl shadow-lg shadow-violet-900/20 relative overflow-hidden group">
                    <div className="absolute top-0 right-0 p-4 opacity-10 group-hover:opacity-20 transition-opacity">
                        <Users size={80} />
                    </div>
                    <div>
                        <p className="text-indigo-100 text-sm font-medium mb-1">Total Users</p>
                        <h3 className="text-3xl font-bold text-white">{stats.total}</h3>
                        <div className="mt-4 flex items-center gap-2">
                            <span className="bg-white/20 text-white text-xs px-2 py-0.5 rounded-full">+12%</span>
                            <span className="text-indigo-200 text-xs">from last month</span>
                        </div>
                    </div>
                </div>

                <div className="bg-gradient-to-br from-emerald-500 to-teal-600 p-6 rounded-2xl shadow-lg shadow-emerald-900/20 relative overflow-hidden group">
                    <div className="absolute top-0 right-0 p-4 opacity-10 group-hover:opacity-20 transition-opacity">
                        <Activity size={80} />
                    </div>
                    <div>
                        <p className="text-emerald-100 text-sm font-medium mb-1">New This Week</p>
                        <h3 className="text-3xl font-bold text-white">{stats.newThisWeek}</h3>
                        <div className="mt-4 flex items-center gap-2">
                            <span className="bg-white/20 text-white text-xs px-2 py-0.5 rounded-full">Trending</span>
                            <span className="text-emerald-100 text-xs">User growth up</span>
                        </div>
                    </div>
                </div>

                <div className="bg-gradient-to-br from-blue-500 to-cyan-600 p-6 rounded-2xl shadow-lg shadow-blue-900/20 relative overflow-hidden group">
                    <div className="absolute top-0 right-0 p-4 opacity-10 group-hover:opacity-20 transition-opacity">
                        <Shield size={80} />
                    </div>
                    <div>
                        <p className="text-blue-100 text-sm font-medium mb-1">Active Accounts</p>
                        <h3 className="text-3xl font-bold text-white">{stats.active}</h3>
                        <div className="mt-4 flex items-center gap-2">
                            <span className="bg-white/20 text-white text-xs px-2 py-0.5 rounded-full">98%</span>
                            <span className="text-blue-100 text-xs">Engagement rate</span>
                        </div>
                    </div>
                </div>
            </div>

            {/* User List View */}
            <div className="bg-slate-900 border border-slate-800 rounded-2xl overflow-hidden shadow-xl">
                <div className="overflow-x-auto">
                    <table className="w-full text-left border-collapse">
                        <thead>
                            <tr className="border-b border-slate-800 bg-slate-900/50">
                                <th className="px-6 py-4 text-xs font-semibold uppercase tracking-wider text-slate-400">User Identity</th>
                                <th className="px-6 py-4 text-xs font-semibold uppercase tracking-wider text-slate-400 hidden lg:table-cell">Contact Info</th>
                                <th className="px-6 py-4 text-xs font-semibold uppercase tracking-wider text-slate-400 text-center">Role</th>
                                <th className="px-6 py-4 text-xs font-semibold uppercase tracking-wider text-slate-400 text-center">Status</th>
                                <th className="px-6 py-4 text-right text-xs font-semibold uppercase tracking-wider text-slate-400">Actions</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-800">
                            {loading ? (
                                <tr>
                                    <td colSpan="5" className="py-20 text-center">
                                        <Loader2 className="animate-spin text-violet-500 mx-auto mb-4" size={32} />
                                        <p className="text-sm font-medium text-slate-400">Loading user data...</p>
                                    </td>
                                </tr>
                            ) : filteredUsers.length > 0 ? filteredUsers.map((user, idx) => (
                                <motion.tr
                                    key={user._id}
                                    initial={{ opacity: 0 }}
                                    animate={{ opacity: 1 }}
                                    transition={{ delay: idx * 0.02 }}
                                    className="group hover:bg-slate-800/50 transition-colors"
                                >
                                    <td className="px-6 py-4">
                                        <div className="flex items-center gap-4">
                                            <div className={`w-10 h-10 rounded-full flex items-center justify-center text-white font-bold shadow-lg ${user.role === 'admin'
                                                ? 'bg-gradient-to-br from-orange-400 to-rose-500 shadow-orange-500/20'
                                                : 'bg-gradient-to-br from-violet-500 to-indigo-500 shadow-violet-500/20'
                                                }`}>
                                                {user.name.charAt(0).toUpperCase()}
                                            </div>
                                            <div>
                                                <h3 className="text-sm font-bold text-white group-hover:text-violet-400 transition-colors">{user.name}</h3>
                                                <p className="text-xs text-slate-500 font-mono mt-0.5">ID: {user._id.slice(-6)}</p>
                                            </div>
                                        </div>
                                    </td>
                                    <td className="px-6 py-4 hidden lg:table-cell">
                                        <div className="space-y-1">
                                            <div className="flex items-center gap-2 text-slate-400 text-xs font-medium">
                                                <Mail size={12} className="text-slate-500" /> {user.email}
                                            </div>
                                            {user.phone && (
                                                <div className="flex items-center gap-2 text-slate-500 text-xs">
                                                    <Phone size={12} /> {user.phone}
                                                </div>
                                            )}
                                        </div>
                                    </td>
                                    <td className="px-6 py-4 text-center">
                                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${user.role === 'admin'
                                            ? 'bg-orange-500/10 text-orange-400 border-orange-500/20'
                                            : 'bg-slate-800 text-slate-400 border-slate-700'
                                            }`}>
                                            {user.role}
                                        </span>
                                    </td>
                                    <td className="px-6 py-4 text-center">
                                        <button
                                            onClick={() => toggleStatus(user)}
                                            className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold transition-all border ${user.status === 'active'
                                                ? 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20 hover:bg-emerald-500/20'
                                                : 'bg-rose-500/10 text-rose-400 border-rose-500/20 hover:bg-rose-500/20'
                                                }`}
                                        >
                                            <span className={`w-1.5 h-1.5 rounded-full ${user.status === 'active' ? 'bg-emerald-500 animate-pulse' : 'bg-rose-500'}`}></span>
                                            {user.status}
                                        </button>
                                    </td>
                                    <td className="px-6 py-4 text-right">
                                        <div className="flex items-center justify-end gap-2">
                                            <button
                                                onClick={() => handleEdit(user)}
                                                className="p-2 text-slate-400 hover:text-white hover:bg-violet-500 rounded-lg transition-all"
                                                title="Edit User"
                                            >
                                                <Edit size={16} />
                                            </button>
                                            <button
                                                onClick={() => handleDeleteUser(user._id)}
                                                className="p-2 text-slate-400 hover:text-white hover:bg-rose-500 rounded-lg transition-all"
                                                title="Delete User"
                                            >
                                                <Trash2 size={16} />
                                            </button>
                                        </div>
                                    </td>
                                </motion.tr>
                            )) : (
                                <tr>
                                    <td colSpan="5" className="py-20 text-center">
                                        <div className="w-16 h-16 bg-slate-800/50 rounded-full flex items-center justify-center mx-auto mb-4">
                                            <Users className="text-slate-600" size={32} />
                                        </div>
                                        <h3 className="text-white font-medium">No users found</h3>
                                        <p className="text-slate-500 text-sm mt-1">Try adjusting your search terms</p>
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Edit User Modal */}
            <AnimatePresence>
                {isEditModalOpen && (
                    <div className="fixed inset-0 z-[100] flex items-center justify-center p-6">
                        <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            onClick={closeModal}
                            className="absolute inset-0 bg-slate-950/80 backdrop-blur-sm"
                        />
                        <motion.div
                            initial={{ opacity: 0, scale: 0.95, y: 20 }}
                            animate={{ opacity: 1, scale: 1, y: 0 }}
                            exit={{ opacity: 0, scale: 0.95, y: 20 }}
                            className="bg-slate-900 border border-slate-800 w-full max-w-lg rounded-2xl shadow-2xl overflow-hidden relative z-10"
                        >
                            <div className="p-6 border-b border-slate-800 flex items-center justify-between bg-slate-900/50">
                                <h2 className="text-xl font-bold text-white">Edit User Profile</h2>
                                <button onClick={closeModal} className="p-2 text-slate-400 hover:text-white hover:bg-slate-800 rounded-lg transition-colors">
                                    <X size={20} />
                                </button>
                            </div>

                            <form onSubmit={handleUpdateUser} className="p-6 space-y-6">
                                <div className="space-y-2">
                                    <label className="text-xs font-semibold text-slate-400 uppercase tracking-wider">User Identity</label>
                                    <input
                                        type="text"
                                        value={formData.name}
                                        disabled
                                        className="w-full bg-slate-800/50 border border-slate-700 rounded-xl px-4 py-3 text-slate-300 focus:outline-none cursor-not-allowed"
                                    />
                                    <p className="text-[10px] text-slate-500">Identity fields cannot be modified here.</p>
                                </div>

                                <div className="grid grid-cols-2 gap-6">
                                    <div className="space-y-2">
                                        <label className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Role</label>
                                        <select
                                            value={formData.role}
                                            onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                                            className="w-full bg-slate-800 border border-slate-700 rounded-xl px-4 py-3 text-white focus:border-violet-500 focus:ring-1 focus:ring-violet-500 outline-none appearance-none cursor-pointer"
                                        >
                                            <option value="user">User</option>
                                            <option value="admin">Administrator</option>
                                        </select>
                                    </div>
                                    <div className="space-y-2">
                                        <label className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Status</label>
                                        <select
                                            value={formData.status}
                                            onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                                            className="w-full bg-slate-800 border border-slate-700 rounded-xl px-4 py-3 text-white focus:border-violet-500 focus:ring-1 focus:ring-violet-500 outline-none appearance-none cursor-pointer"
                                        >
                                            <option value="active">Active</option>
                                            <option value="disabled">Disabled</option>
                                        </select>
                                    </div>
                                </div>

                                <div className="pt-4">
                                    <button
                                        type="submit"
                                        className="w-full py-3.5 bg-violet-600 hover:bg-violet-700 text-white font-bold rounded-xl transition-all shadow-lg shadow-violet-600/20 active:scale-[0.98]"
                                    >
                                        Save Changes
                                    </button>
                                </div>
                            </form>
                        </motion.div>
                    </div>
                )}
            </AnimatePresence>
        </div>
    );
};

export default UserManager;
